import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function InstructorPollManager() {
	const { user } = useAuth();
	const [title, setTitle] = useState('');
	const [session, setSession] = useState(null);
	const [questions, setQuestions] = useState([{ text: '' , groupLabel: '' }]);
	const [groups, setGroups] = useState(['Group 1', 'Group 2']);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
    const [activeSessions, setActiveSessions] = useState([]);
    const [loadingActive, setLoadingActive] = useState(false);

	const shell = { maxWidth: 1040, margin: '0 auto', padding: 28 };
	const card = { background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 24 };
	const input = { width: '100%', padding: '12px 14px', border: '1px solid var(--border-color)', borderRadius: 0, background: 'var(--background-secondary)' };
	const btn = { padding: '10px 14px', borderRadius: 0, border: '1px solid var(--primary-color)', background: 'var(--primary-color)', color: 'var(--text-inverse)', fontWeight: 600, cursor: 'pointer' };
	const btnGhost = { padding: '9px 12px', borderRadius: 0, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.9rem', lineHeight: 1.2, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', cursor: 'pointer' };

	const canManage = useMemo(() => user && (user.role === 'instructor' || user.role === 'admin'), [user]);

	const loadActive = async () => {
		setLoadingActive(true);
		try {
			const res = await api.listActivePollSessions();
			setActiveSessions(res.sessions || []);
		} catch (e) {
			// non-fatal
		} finally {
			setLoadingActive(false);
		}
	};

	useEffect(() => { loadActive(); const t = setInterval(loadActive, 15000); return () => clearInterval(t); }, []);

	const createSession = async (e) => {
		e.preventDefault();
		if (!title.trim()) return;
		setSaving(true); setError('');
		try {
			const res = await api.createPollSession(title.trim());
			setSession(res.session);
		} catch (e) {
			setError(e.message || 'Failed to create session');
		} finally {
			setSaving(false);
		}
	};

	const saveQuestions = async (e) => {
		e.preventDefault();
		if (!session) return;
		const qs = questions
			.map(q => ({ text: (q.text || '').trim(), groupLabel: (q.groupLabel || '').trim() }))
			.filter(q => q.text && q.groupLabel);
		if (qs.length === 0) return;
		setSaving(true); setError('');
		try {
			// Save groups first
			await api.setPollGroups(session.id, groups.map(g => String(g).trim()).filter(Boolean));
			await api.addPollQuestions(session.id, qs);
			// no-op; keep in local state
		} catch (e) {
			setError(e.message || 'Failed to save questions');
		} finally {
			setSaving(false);
		}
	};

	if (!canManage) {
		return <div style={{ padding: 16 }}>You do not have permission to access this page.</div>;
	}

	return (
		<div style={shell}>
			<div style={{ ...card, marginBottom: 24 }}>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
					<div style={{ fontWeight: 700 }}>Active Polls</div>
					<button type="button" style={btnGhost} onClick={loadActive} disabled={loadingActive}>{loadingActive ? 'Refreshing…' : 'Refresh'}</button>
				</div>
				{activeSessions && activeSessions.length > 0 ? (
					<div style={{ display: 'grid', gap: 8 }}>
						{activeSessions.map(s => (
							<div key={s.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', border: '1px solid var(--border-color)', borderRadius: 0, padding: 12 }}>
								<div>
									<div style={{ fontWeight: 600 }}>{s.title}</div>
									<div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Code: {s.code} {typeof s.answers === 'number' ? `· Answers: ${s.answers}` : ''}</div>
								</div>
								<div style={{ display: 'flex', gap: 8 }}>
									<button type="button" onClick={() => window.open(`/portal/instructor/live-poll?code=${encodeURIComponent(s.code)}`, '_blank')} style={btnGhost}>Open Board</button>
									<button type="button" onClick={() => window.open('/portal/student/group-poll', '_blank')} style={btnGhost}>Student Page</button>
								</div>
							</div>
						))}
					</div>
				) : (
					<div style={{ color: 'var(--text-secondary)' }}>No active polls yet.</div>
				)}
			</div>
			<div style={{ marginBottom: 16 }}>
				<h1 style={{ fontSize: 36, lineHeight: 1.2, margin: 0 }}>Create Group Poll</h1>
				<p style={{ color: 'var(--text-secondary)', marginTop: 6 }}>Generate a session and add your questions</p>
			</div>

			<div style={{ ...card, marginBottom: 24 }}>
				<form onSubmit={createSession}>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16 }}>
						<div>
							<label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Title</label>
							<input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Seerah Week 3" style={input} />
						</div>
						<div style={{ display: 'flex', alignItems: 'flex-end' }}>
							<button type="submit" disabled={saving || !!session} style={{ ...btn, opacity: saving ? 0.7 : 1 }}>{session ? 'Created' : (saving ? 'Creating…' : 'Create Session')}</button>
						</div>
					</div>
				</form>
			</div>

			{session && (
				<div style={{ ...card, marginBottom: 28 }}>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
						<div>
							<div style={{ fontWeight: 700 }}>Session: {session.title}</div>
							<div style={{ color: 'var(--text-secondary)' }}>Code: {session.code}</div>
						</div>
						<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
							<button
								type="button"
								onClick={() => window.open('/portal/instructor/live-poll', '_blank')}
								style={btnGhost}
								onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
								onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
							>
								Open Live Board
							</button>
							<button
								type="button"
								onClick={() => window.open('/portal/student/group-poll', '_blank')}
								style={btnGhost}
								onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
								onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
							>
								Student Page
							</button>
							<button
								type="button"
								onClick={async () => { try { await api.updatePollSession(session.id, { title }); alert('Title saved'); } catch (e) { alert(e.message || 'Failed to save'); } }}
								style={btnGhost}
								onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
								onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
							>
								Save Title
							</button>
							<button
								type="button"
								onClick={async () => { if (!window.confirm('Delete this poll session? This will hide it for everyone.')) return; try { await api.deletePollSession(session.id); window.location.href = '/portal/instructor/polls'; } catch (e) { alert(e.message || 'Failed to delete'); } }}
								style={{ ...btnGhost, color: 'var(--error-dark)', borderColor: 'var(--error-color)' }}
								onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
								onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
							>
								Delete Poll
							</button>
						</div>
					</div>
				</div>
			)}

			{session && (
				<div style={{ ...card, marginBottom: 24 }}>
					<div style={{ fontWeight: 700, marginBottom: 12 }}>Groups</div>
					{groups.map((g, i) => (
						<div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 8 }}>
							<input value={g} onChange={e => setGroups(prev => prev.map((v, idx) => idx === i ? e.target.value : v))} style={input} placeholder={`Group ${i+1}`} />
							<div style={{ display: 'flex', alignItems: 'end' }}>
								<button type="button" style={btnGhost} onClick={() => setGroups(prev => prev.filter((_, idx) => idx !== i))} disabled={groups.length <= 1}>Remove</button>
							</div>
						</div>
					))}
					<div style={{ display: 'flex', gap: 12 }}>
						<button type="button" style={btnGhost} onClick={() => setGroups(prev => [...prev, `Group ${prev.length+1}`])}>+ Add Group</button>
					</div>
				</div>
			)}

			{session && (
				<div style={{ ...card, marginBottom: 24 }}>
					<form onSubmit={saveQuestions}>
						<div style={{ fontWeight: 700, marginBottom: 12 }}>Questions</div>
						{questions.map((q, i) => (
							<div key={i} style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr 220px auto', gap: 12 }}>
								<div>
									<label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Question {i + 1}</label>
									<input value={q.text} onChange={e => {
										const next = [...questions];
										next[i] = { ...next[i], text: e.target.value };
										setQuestions(next);
									}} style={input} placeholder={`Type question ${i + 1}`} />
								</div>
								<div>
									<label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Group</label>
									<select value={q.groupLabel} onChange={e => {
										const next = [...questions];
										next[i] = { ...next[i], groupLabel: e.target.value };
										setQuestions(next);
									}} style={{ ...input, padding: '12px 10px' }}>
										<option value="">Select group…</option>
										{groups.map(g => <option key={g} value={g}>{g}</option>)}
									</select>
								</div>
								<div style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
									<button type="button" onClick={() => setQuestions(prev => {
										const next = prev.filter((_, idx) => idx !== i);
										return next.length ? next : [{ text: '', groupLabel: '' }];
									})} disabled={questions.length === 1} style={btnGhost}>Remove</button>
								</div>
							</div>
						))}
						<div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
							<button type="button" onClick={() => setQuestions(prev => [...prev, { text: '', groupLabel: '' }])} style={btnGhost}>+ Add Question</button>
							<button type="submit" disabled={saving} style={{ ...btn, padding: '12px 20px', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save Questions'}</button>
						</div>
					</form>
				</div>
			)}

			{error ? <div style={{ color: 'var(--error-color)', marginTop: 8, fontWeight: 600 }}>{error}</div> : null}
		</div>
	);
}


